"use client";

import * as React from "react";
import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useColorScheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { List as ListIcon } from "@phosphor-icons/react/dist/ssr/List";
import { MagnifyingGlass as MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { NavItemConfig } from "@/types/nav";
import { paths } from "@/paths";
import { useDialog } from "@/hooks/use-dialog";
import { usePopover } from "@/hooks/use-popover";

import { useOverview, useUser } from "../../../../api/hooks";
import { getDeviceInfos } from "../../../../lib/device";
import { Mode } from "../../../../styles/theme/types";
import { User } from "../../../../types/user";
import type { Language } from "../language-popover";
import { languageFlags, LanguagePopover } from "../language-popover";
import { LoadingRoundValue } from "../loadingValue";
import { MobileNav } from "../mobile-nav";
import { SearchDialog } from "../search-dialog";
import { UserPopover } from "../user-popover/user-popover";

export interface MainNavProps {
	items: NavItemConfig[];
}

export function MainNav({ items }: MainNavProps): React.JSX.Element {
	const queryClient = useQueryClient();

	const { isAuthenticated } = useAuth0();
	//const { settings } = useSettings();

	const { mode, setMode } = useColorScheme();

    const { data: userData, isLoading: isLoadingUser } = useUser(); 
    const {data: overviewData} = useOverview(true); 

	const [openNav, setOpenNav] = React.useState<boolean>(false);

	useEffect(() => {
		if (userData && !isLoadingUser) {
			setMode((userData?.settings?.theme as Mode) ?? "light");
		}
	}, [isLoadingUser, setMode, userData]);

	return (
		<React.Fragment>
			<Box
				component="header"
				sx={{
					"--MainNav-background": "var(--mui-palette-background-default)",
					//"--MainNav-divider": "var(--mui-palette-divider)",
					bgcolor: "var(--MainNav-background)",
					left: 0,
					position: "sticky",
					//position: "absolute",
					pt: { lg: "var(--Layout-gap)" },
					top: 0,
					width: "100%",
					zIndex: "var(--MainNav-zIndex)",
				}}
			>
				<Box
					sx={{
						borderBottom: "1px solid var(--MainNav-divider)",
						display: "flex",
						flex: "1 1 auto",
						minHeight: "var(--MainNav-height)",
						px: { xs: 2, lg: 3 },
						py: 1,
					}}
				>
					<Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: "1 1 auto" }}>
						<IconButton
							onClick={(): void => {
								setOpenNav(true);
							}}
							sx={{ display: { lg: "none" } }}
						>
							<ListIcon />
						</IconButton>

						{DeviceHeader(queryClient)}

						{/*<SearchButton />*/}
					</Stack>

					<Stack
						direction="row"
						spacing={2}
						sx={{ alignItems: "flex-start", flex: "1 1 auto", justifyContent: "flex-end" }}
					>
						{/*<DeviasButton />*/}

						{/*<LanguageSwitch />*/}

						{isAuthenticated && userData && !isLoadingUser ? (
							<UserButton user={userData} />
						) : (
							LoadingRoundValue("40px", "40px", "30px")
						)}
					</Stack>
				</Box>
			</Box>

			<MobileNav
				items={items}
				onClose={() => {
					setOpenNav(false);
				}}
				open={openNav}
			/>
		</React.Fragment>
	);
}

function DeviceHeader(queryClient: QueryClient) {
	const { deviceId: id } = useParams<{ deviceId: string }>();
	const { deviceId, deviceType } = getDeviceInfos(queryClient, id);

	if (!deviceId || !deviceType) {
		return <></>;
	}

	return;
	/*  <Paper
            elevation={1}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderRadius: 1,
                minHeight: 48,
                backgroundColor: "var(--mui-palette-background-default)", // heller Hintergrund
                width: "fit-content",
                //maxWidth: 300,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>*/
	{
		/*<HexagonIcon color="primary" fontSize="small" />*/
	}
	{
		/*<Typography fontWeight="bold">{deviceType}</Typography>
            </Box>
            <Box sx={{ mx: 1 }} />

            <Typography variant="body2" color="primary">
                {deviceId}
            </Typography>
        </Paper>*/
	}
}

function DeviasButton(): React.JSX.Element {
	//const navigate = useNavigate();

	return (
		<React.Fragment>
			<Tooltip title="Devias Dashboard">
				<IconButton
					onClick={() => {
						window.open(paths.dashboard.devias);
					}}
					sx={{ display: { xs: "none", lg: "inline-flex" } }}
				>
					<MagnifyingGlassIcon />
				</IconButton>
			</Tooltip>
		</React.Fragment>
	);
}

function SearchButton(): React.JSX.Element {
	const dialog = useDialog();

	return (
		<React.Fragment>
			<Tooltip title="Search">
				<IconButton onClick={dialog.handleOpen} sx={{ display: { xs: "none", lg: "inline-flex" } }}>
					<MagnifyingGlassIcon />
				</IconButton>
			</Tooltip>
			<SearchDialog onClose={dialog.handleClose} open={dialog.open} />
		</React.Fragment>
	);
}

//function ContactsButton(): React.JSX.Element {
//    const popover = usePopover<HTMLButtonElement>();

//    return (
//        <React.Fragment>
//            <Tooltip title="Contacts">
//                <IconButton onClick={popover.handleOpen} ref={popover.anchorRef}>
//                    <UsersIcon />
//                </IconButton>
//            </Tooltip>
//            <ContactsPopover anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
//        </React.Fragment>
//    );
//}

//function NotificationsButton(): React.JSX.Element {
//    const popover = usePopover<HTMLButtonElement>();

//    return (
//        <React.Fragment>
//            <Tooltip title="Notifications">
//                <Badge
//                    color="error"
//                    sx={{ "& .MuiBadge-dot": { borderRadius: "50%", height: "10px", right: "6px", top: "6px", width: "10px" } }}
//                    variant="dot"
//                >
//                    <IconButton onClick={popover.handleOpen} ref={popover.anchorRef}>
//                        <BellIcon />
//                    </IconButton>
//                </Badge>
//            </Tooltip>
//            <NotificationsPopover anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
//        </React.Fragment>
//    );
//}

function LanguageSwitch(): React.JSX.Element {
	const { i18n } = useTranslation();
	const popover = usePopover<HTMLButtonElement>();
	const language = (i18n.language || "de") as Language;
	const flag = languageFlags[language];

	return (
		<React.Fragment>
			<Tooltip title="Language">
				<IconButton
					onClick={popover.handleOpen}
					ref={popover.anchorRef}
					sx={{ display: { xs: "none", lg: "inline-flex" } }}
				>
					<Box sx={{ height: "24px", width: "24px" }}>
						<Box alt={language} component="img" src={flag} sx={{ height: "auto", width: "100%" }} />
					</Box>
				</IconButton>
			</Tooltip>
			<LanguagePopover anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
		</React.Fragment>
	);
}

function UserButton({ user }: { user: User }): React.JSX.Element {
	const popover = usePopover<HTMLButtonElement>();

    const avatarSrc =
        user?.avatar
            ? user.avatar.startsWith("data:")
                ? user.avatar
                : `data:image/jpeg;base64,${user.avatar}` // oder png, siehe unten
            : undefined;


	return (
		<React.Fragment>
			<Box
				component="button"
				onClick={popover.handleOpen}
				ref={popover.anchorRef}
				sx={{ border: "none", background: "transparent", cursor: "pointer", p: 0 }}
			>
				{/*<Badge*/}
				{/*	anchorOrigin={{ horizontal: "right", vertical: "bottom" }}*/}
				{/*	color="success"*/}
				{/*	sx={{*/}
				{/*		"& .MuiBadge-dot": {*/}
				{/*			border: "2px solid var(--MainNav-background)",*/}
				{/*			borderRadius: "50%",*/}
				{/*			bottom: "6px",*/}
				{/*			height: "12px",*/}
				{/*			right: "6px",*/}
				{/*			width: "12px",*/}
				{/*		},*/}
				{/*	}}*/}
				{/*	variant="dot"*/}
                {/*>*/}

                {
                    avatarSrc
                        ? <Avatar src={avatarSrc} />
                        : <Avatar /> // kein Avatar bisher definiert!

                }

                
				{/*</Badge>*/}
			</Box>
			<UserPopover user={user} anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
		</React.Fragment>
	);
}
